import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2,
  Shield,
  CheckCircle2,
  ScanSearch,
  ChevronDown,
  ChevronRight,
  CircleDot,
  CircleCheck,
  CircleAlert,
  HelpCircle,
  FileCheck,
  BookOpen,
  ClipboardCheck,
  Database,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { useChatStopped } from '@/components/chat/chat-stopped-context'
import {
  PolicyPipelineTimeline,
  type PolicyPipelineEvent,
} from '@/components/chat/messages'
import { ToolErrorGuidance } from '../ToolErrorGuidance'

interface PolicyToolPartProps {
  part: any
  icon?: React.ReactNode
}

/**
 * Collapsible debug panel showing tool input args and output.
 * Only renders in dev mode.
 */
export function ToolDebugPanel({
  toolName,
  args,
  result,
  isComplete,
}: {
  toolName?: string
  args: any
  result: any
  isComplete: boolean
}) {
  const [open, setOpen] = useState(false)
  if (!import.meta.env.DEV) return null

  return (
    <div className='mt-1 ml-5'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='text-muted-foreground/60 hover:text-muted-foreground cursor-pointer font-mono text-[10px]'
      >
        {open
          ? `▾ hide debug${toolName ? ` · ${toolName}` : ''}`
          : `▸ debug${toolName ? ` · ${toolName}` : ''}`}
      </button>
      {open && (
        <div className='text-muted-foreground border-border bg-surface-subtle mt-1 max-h-[300px] space-y-1 overflow-auto rounded-sm border border-dashed p-2 font-mono text-[10px]'>
          {toolName && (
            <div>
              <span className='font-semibold'>tool:</span>{' '}
              <span className='text-text-secondary'>{toolName}</span>
            </div>
          )}
          <div>
            <span className='font-semibold'>input:</span>
            <pre className='break-all whitespace-pre-wrap'>
              {JSON.stringify(args, null, 2)}
            </pre>
          </div>
          {isComplete && (
            <div>
              <span className='font-semibold'>output:</span>
              <pre className='break-all whitespace-pre-wrap'>
                {typeof result === 'string'
                  ? result.slice(0, 2000)
                  : JSON.stringify(result, null, 2)?.slice(0, 2000)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Helpers for extracting state/result from both v6 and legacy formats.
 * v6: part.state = 'input-streaming' | 'input-available' | 'output-available', part.output
 * Legacy: part.toolInvocation.state = 'call' | 'result', part.toolInvocation.result
 */
function getToolState(part: any): {
  isComplete: boolean
  isError: boolean
  result: any
  rawOutput: any
  args: any
} {
  // v6 tool-input-error part (separate part type, not a state on a tool call)
  if (part.type === 'tool-input-error') {
    return {
      isComplete: true,
      isError: true,
      result: null,
      rawOutput: part.errorText ?? null,
      args: part.input ?? {},
    }
  }
  // v6 format: flat properties on part
  if (part.state) {
    const isComplete =
      part.state === 'output-available' || part.state === 'error'
    const isError = part.state === 'error'
    const rawOutput = isComplete
      ? (part.output ?? part.errorText ?? null)
      : null
    let result = null
    if (isComplete && part.output != null) {
      try {
        result =
          typeof part.output === 'string'
            ? JSON.parse(part.output)
            : part.output
      } catch {
        /* ignore */
      }
    }
    return { isComplete, isError, result, rawOutput, args: part.input ?? {} }
  }
  // Legacy format: part.toolInvocation
  const ti = part.toolInvocation
  if (ti) {
    const isComplete = ti.state === 'result' || ti.state === 'error'
    const isError = ti.state === 'error'
    const rawOutput = isComplete ? ti.result : null
    let result = null
    if (isComplete && ti.result) {
      try {
        result =
          typeof ti.result === 'string' ? JSON.parse(ti.result) : ti.result
      } catch {
        /* ignore */
      }
    }
    return { isComplete, isError, result, rawOutput, args: ti.args ?? {} }
  }
  return {
    isComplete: false,
    isError: false,
    result: null,
    rawOutput: null,
    args: {},
  }
}

/** Check if a completed tool result represents a failure. */
function isToolError(isComplete: boolean, result: any): boolean {
  if (!isComplete || !result) return false
  if (result.ok === false) return true
  if (result.success === false) return true
  if (result.error) return true
  return false
}

/** Extract a human-readable error message from a tool result. */
function getErrorMessage(result: any): string | null {
  if (!result) return null
  return result.error ?? result.message ?? result.errorCode ?? null
}

/**
 * Renders the generatePolicy tool invocation in the chat.
 * Shows a loading state while generating, then a success message.
 * Dispatches 'screenata:policy-generated' event when docId is available.
 */
export function GeneratePolicyMessage({ part }: PolicyToolPartProps) {
  const dispatched = useRef(false)
  const lastDraftPayloadRef = useRef<string | null>(null)
  const { isComplete, result } = getToolState(part)
  const isPreliminary = Boolean((part as any)?.preliminary)
  const preliminaryResult = (() => {
    if (!isPreliminary) return null
    const raw = (part as any)?.output
    if (!raw) return null
    if (typeof raw === 'object') return raw
    if (typeof raw !== 'string') return null
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  })()

  // Accumulate pipeline status events across preliminary updates so we can render the same timeline UI the dedicated policy-chat endpoint uses — the AI SDK only exposes the latest yielded value, so we track history here.
  const [pipelineEvents, setPipelineEvents] = useState<PolicyPipelineEvent[]>(
    []
  )
  const chatStopped = useChatStopped()
  const hasPartialRef = useRef(false)
  const savedStageInjectedRef = useRef(false)
  useEffect(() => {
    if (!preliminaryResult) return
    if (preliminaryResult.type === 'partial') {
      hasPartialRef.current = true
      return
    }
    if (preliminaryResult.type === 'status') {
      const event: PolicyPipelineEvent = {
        stage: preliminaryResult.stage ?? 'unknown',
        state: preliminaryResult.state,
        label: preliminaryResult.label ?? '',
        detail: preliminaryResult.detail,
        sections: preliminaryResult.sections,
        findings: preliminaryResult.findings,
      }
      // Mirror the latest stage out to the in-editor banner.
      if (event.label) {
        window.dispatchEvent(
          new CustomEvent('screenata:policy-stage', {
            detail: {
              stage: event.stage,
              state: event.state,
              label: event.label,
              detail: event.detail,
            },
          })
        )
      }
      setPipelineEvents((prev) => {
        // Once we see the first status event, the partial/writing phase is over — synthesize a "saved" milestone so the timeline starts with "Policy draft saved" like the dedicated-endpoint flow does (the tool path doesn't emit one itself).
        let next = prev
        if (
          !savedStageInjectedRef.current &&
          hasPartialRef.current &&
          prev.length === 0
        ) {
          savedStageInjectedRef.current = true
          next = [
            {
              stage: 'saved',
              state: 'end',
              label: 'Policy draft saved',
              detail: 'Processing compliance checks.',
            },
          ]
        }
        const last = next[next.length - 1]
        if (
          last &&
          last.stage === event.stage &&
          last.state === event.state &&
          last.label === event.label &&
          last.detail === event.detail
        ) {
          return next
        }
        return [...next, event]
      })
    }
  }, [preliminaryResult])

  // When the tool completes, clear the live preview so the editor swaps to the server-saved (post-revision) TipTap doc — otherwise liveDraftContent stays pinned to the pre-revision partial stream and the viewer looks truncated/stale.
  const clearedDraftRef = useRef(false)
  useEffect(() => {
    console.log('[GeneratePolicyMessage] clear effect check', {
      isComplete,
      success: result?.success,
      alreadyCleared: clearedDraftRef.current,
    })
    if (!isComplete || !result?.success) return
    if (clearedDraftRef.current) return
    clearedDraftRef.current = true
    console.log('[GeneratePolicyMessage] dispatching clear events')
    window.dispatchEvent(
      new CustomEvent('screenata:policy-draft-stream', {
        detail: { clear: true },
      })
    )
    window.dispatchEvent(
      new CustomEvent('screenata:policy-stage', {
        detail: { clear: true },
      })
    )
  }, [isComplete, result])

  const docId = result?.success
    ? (result?.policyDocumentId ?? result?.docId ?? null)
    : null

  // Dispatch event when docId becomes available
  useEffect(() => {
    if (docId && !dispatched.current) {
      dispatched.current = true
      window.dispatchEvent(
        new CustomEvent('screenata:policy-generated', {
          detail: { policyDocumentId: docId },
        })
      )
    }
  }, [docId])

  useEffect(() => {
    if (!isPreliminary || preliminaryResult?.type !== 'partial') return

    const policyContent =
      typeof preliminaryResult?.policyContent === 'string'
        ? preliminaryResult.policyContent
        : null
    const policyContentDelta =
      typeof preliminaryResult?.policyContentDelta === 'string'
        ? preliminaryResult.policyContentDelta
        : null

    if (!policyContent && !policyContentDelta) return

    // Dedup by content length — content only grows during streaming
    const payloadKey = `${policyContent?.length ?? 0}:${policyContentDelta?.length ?? 0}`
    if (lastDraftPayloadRef.current === payloadKey) return
    lastDraftPayloadRef.current = payloadKey

    window.dispatchEvent(
      new CustomEvent('screenata:policy-draft-stream', {
        detail: {
          policyContent,
          policyContentDelta,
        },
      })
    )
  }, [isPreliminary, preliminaryResult])

  // Regeneration approval is handled via tool-approval-request part in messages.tsx

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='generatePolicy'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  // Don't show anything when the tool is paused for approval or still building input.
  const partState = (part as any)?.state
  if (
    partState === 'approval-requested' ||
    partState === 'input-streaming' ||
    partState === 'input-available' ||
    partState === 'partial-call'
  ) {
    return null
  }

  const hasStatusEvents = pipelineEvents.length > 0
  const hasSeenStatus = hasStatusEvents || hasPartialRef.current
  const doneSummary =
    isComplete && result?.success
      ? {
          claimsRecorded: result?.claimsRecorded,
          reviewStatus: result?.reviewStatus,
        }
      : null
  const isAborted = chatStopped && !isComplete

  // Once we've seen any pipeline status, switch to the shared timeline view.
  if (hasSeenStatus || doneSummary || isAborted) {
    return (
      <PolicyPipelineTimeline
        events={pipelineEvents}
        isStreaming={!isComplete && !chatStopped}
        isAborted={isAborted && !isComplete}
        doneSummary={doneSummary}
      />
    )
  }

  // Fallback for the brief window before any preliminary lands.
  return (
    <div className='flex items-center gap-2 py-1 text-xs'>
      <Loader2 className='text-muted-foreground h-3.5 w-3.5 shrink-0 animate-spin' />
      <span className='text-foreground/80 font-medium'>
        Generating policy...
      </span>
    </div>
  )
}

export function UpdatePolicySectionMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result, args } = getToolState(part)
  const sectionKey = args?.sectionKey ?? result?.sectionKey ?? 'section'
  const fallbackToFullRegen = !!result?.fallbackToFullRegen
  const dispatched = useRef(false)

  // When section update succeeds, dispatch event so the editor picks up
  // the new content via TipTap API instead of remounting.
  useEffect(() => {
    const docId = result?.policyDocumentId
    if (isComplete && result?.success && docId && !dispatched.current) {
      dispatched.current = true
      window.dispatchEvent(
        new CustomEvent('screenata:policy-generated', {
          detail: { policyDocumentId: docId },
        })
      )
    }
  }, [isComplete, result])

  let label = `Updating section: ${sectionKey}`
  if (isComplete && result?.success) {
    label = `Section updated: ${sectionKey}`
  } else if (isComplete && fallbackToFullRegen) {
    label = `Section update failed, fallback to full regeneration`
  }

  return (
    <div className='flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-600' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
      )}
      <span className='text-foreground'>{label}</span>
    </div>
  )
}

/**
 * Renders recordEvidence tool invocations compactly.
 */
export function RecordEvidenceMessage({ part }: PolicyToolPartProps) {
  const { isComplete, args } = getToolState(part)

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span className='truncate'>
        {isComplete ? 'Recorded' : 'Recording'}:{' '}
        {args.claimText ?? args.claimType ?? 'evidence claim'}
      </span>
    </div>
  )
}

/**
 * Renders checkPolicyCompliance tool invocations.
 */
export function CheckPolicyComplianceMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result } = getToolState(part)
  const [expanded, setExpanded] = useState(false)

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='checkPolicyCompliance'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const status = String(result?.status ?? '')
  const findings: any[] = Array.isArray(result?.blockingFindings)
    ? result.blockingFindings
    : []

  let label: string
  let iconColor: string
  if (!isComplete) {
    label = 'Reviewing policy compliance...'
    iconColor = ''
  } else if (status === 'PASS') {
    label = 'Policy meets all compliance requirements'
    iconColor = 'text-emerald-500'
  } else if (findings.length > 0) {
    label = `Found ${findings.length} item${findings.length === 1 ? '' : 's'} that need${findings.length === 1 ? 's' : ''} attention`
    iconColor = 'text-status-orange'
  } else {
    label = 'Review complete, some improvements suggested'
    iconColor = 'text-blue-500'
  }

  return (
    <div className='text-muted-foreground py-1 text-xs'>
      <button
        type='button'
        onClick={() => findings.length > 0 && setExpanded((v) => !v)}
        className={`flex w-full items-center gap-2 ${findings.length > 0 ? 'cursor-pointer' : ''}`}
        disabled={findings.length === 0}
      >
        {!isComplete ? (
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
        ) : status === 'PASS' ? (
          <CheckCircle2 className={`h-3.5 w-3.5 ${iconColor} shrink-0`} />
        ) : (
          <CircleAlert className={`h-3.5 w-3.5 ${iconColor} shrink-0`} />
        )}
        <span className='flex-1 text-left'>{label}</span>
        {findings.length > 0 &&
          (expanded ? (
            <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ) : (
            <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ))}
      </button>
      {expanded && findings.length > 0 && (
        <ul className='mt-1.5 ml-5 space-y-1'>
          {findings.map((f: any, i: number) => (
            <li
              key={i}
              className='text-muted-foreground flex items-start gap-1.5 text-xs'
            >
              <CircleAlert className='mt-0.5 h-3 w-3 shrink-0 text-status-orange' />
              <span>
                {f.detail ?? f.title ?? f.summary ?? `Finding ${i + 1}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const SUPPORT_ICON: Record<string, React.ReactNode> = {
  verified: <Shield className='h-3 w-3 shrink-0 text-emerald-600' />,
  attested: <FileCheck className='h-3 w-3 shrink-0 text-blue-500' />,
  weak_match: <CircleAlert className='h-3 w-3 shrink-0 text-status-orange' />,
  unsupported: <CircleDot className='h-3 w-3 shrink-0 text-red-400' />,
}

const EVIDENCE_ICON: Record<string, React.ReactNode> = {
  found: <CircleCheck className='h-3 w-3 shrink-0 text-emerald-500' />,
  partial: <CircleAlert className='h-3 w-3 shrink-0 text-status-orange' />,
  missing: <CircleDot className='h-3 w-3 shrink-0 text-red-400' />,
}

/**
 * Renders buildPolicyPlan tool invocations with expandable sections & questions.
 */
export function BuildPolicyPlanMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result } = getToolState(part)
  const [expanded, setExpanded] = useState(false)

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='buildPolicyPlan'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const sections: any[] = Array.isArray(result?.sections) ? result.sections : []
  const questions: any[] = Array.isArray(result?.batchedQuestions)
    ? result.batchedQuestions
    : []
  const evidence = result?.evidenceSummary

  if (!isComplete) {
    return (
      <div className='flex items-center gap-2 py-1 text-xs'>
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        <span className='font-medium text-status-blue-strong'>
          Planning policy outline...
        </span>
      </div>
    )
  }

  const hasEvidence =
    evidence &&
    (evidence.cloudFacts > 0 ||
      evidence.existingClaims > 0 ||
      evidence.vaultItems > 0 ||
      evidence.linkedTests > 0)

  return (
    <div className='text-xs'>
      {/* Evidence scan result — shown as a separate tool-style line (legacy buildPolicyPlan flow) */}
      {evidence && (
        <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
          {hasEvidence ? (
            <>
              <ScanSearch className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
              <span>
                Evidence found:{' '}
                {[
                  evidence.cloudFacts > 0 &&
                    `${evidence.cloudFacts} cloud facts`,
                  evidence.existingClaims > 0 &&
                    `${evidence.existingClaims} existing claims`,
                  evidence.vaultItems > 0 &&
                    `${evidence.vaultItems} vault items`,
                  evidence.linkedTests > 0 &&
                    `${evidence.linkedTests} linked tests`,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </>
          ) : (
            <>
              <ScanSearch className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
              <span>No existing evidence found</span>
            </>
          )}
        </div>
      )}

      {/* Plan summary — expandable */}
      <div className='py-1'>
        <button
          type='button'
          onClick={() => setExpanded((v) => !v)}
          className='flex w-full cursor-pointer items-center gap-2'
        >
          <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-600' />
          <span className='flex-1 text-left font-medium text-status-green-strong'>
            Plan ready: {sections.length} sections
            {questions.length > 0 ? `, ${questions.length} questions` : ''}
          </span>
          {expanded ? (
            <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ) : (
            <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          )}
        </button>

        {expanded && (
          <div className='mt-2 space-y-3 pl-6'>
            {/* Sections */}
            {sections.map((section: any, i: number) => {
              const items: any[] = Array.isArray(section.items)
                ? section.items
                : []
              const foundCount = items.filter(
                (it: any) => it.evidenceStatus === 'found'
              ).length
              const missingCount = items.filter(
                (it: any) => it.evidenceStatus === 'missing'
              ).length
              return (
                <div key={section.key ?? i}>
                  <div className='text-foreground flex items-center gap-1.5 text-xs font-medium'>
                    <span>{section.title}</span>
                    <span className='text-muted-foreground font-normal'>
                      ({items.length} items
                      {foundCount > 0 ? `, ${foundCount} covered` : ''}
                      {missingCount > 0 ? `, ${missingCount} gaps` : ''})
                    </span>
                  </div>
                  {items.length > 0 && (
                    <div className='mt-1 space-y-0.5'>
                      {items.map((item: any, j: number) => (
                        <div
                          key={item.id ?? j}
                          className='text-muted-foreground flex items-start gap-1.5 text-xs'
                        >
                          {SUPPORT_ICON[item.supportLevel] ??
                            EVIDENCE_ICON[item.evidenceStatus] ?? (
                              <HelpCircle className='h-3 w-3 shrink-0' />
                            )}
                          <span className='leading-relaxed'>
                            {item.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

            {/* Questions */}
            {questions.length > 0 && (
              <div>
                <div className='text-foreground mb-1 text-xs font-medium'>
                  Questions for user
                </div>
                <div className='space-y-0.5'>
                  {questions.map((q: any, i: number) => (
                    <div
                      key={q.itemId ?? i}
                      className='text-muted-foreground flex items-start gap-1.5 text-xs'
                    >
                      <HelpCircle className='mt-0.5 h-3 w-3 shrink-0 text-blue-400' />
                      <span className='leading-relaxed'>{q.question}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Renders generatePlanQuestions tool invocations.
 * Shows generated questions for policy gaps.
 */
export function GeneratePlanQuestionsMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  const questions: any[] = Array.isArray(result?.questions)
    ? result.questions
    : []
  const totalGaps = result?.totalGaps ?? questions.length

  if (!isComplete) {
    return (
      <div className='flex items-center gap-2 py-1 text-xs'>
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        <span className='font-medium text-status-blue-strong'>
          Generating questions for gaps...
        </span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className='flex items-center gap-2 py-1 text-xs'>
        <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
        <span className='text-muted-foreground'>
          Question generation skipped
        </span>
      </div>
    )
  }

  if (questions.length === 0) {
    return (
      <div className='flex items-center gap-2 py-1 text-xs'>
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-600' />
        <span className='font-medium text-status-green-strong'>
          No gaps: all evidence covered
        </span>
      </div>
    )
  }

  return (
    <div className='py-1 text-xs'>
      <button
        type='button'
        onClick={() => setExpanded((v) => !v)}
        className='flex w-full cursor-pointer items-center gap-2 rounded-md'
      >
        <HelpCircle className='h-3.5 w-3.5 shrink-0 text-blue-500' />
        <span className='flex-1 text-left font-medium text-status-blue-strong'>
          {questions.length} questions for {totalGaps} gaps
        </span>
        {expanded ? (
          <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        ) : (
          <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        )}
      </button>

      {expanded && (
        <div className='mt-2 space-y-0.5 pl-6'>
          {questions.map((q: any, i: number) => (
            <div
              key={q.itemId ?? i}
              className='text-muted-foreground flex items-start gap-1.5 text-xs'
            >
              <HelpCircle className='mt-0.5 h-3 w-3 shrink-0 text-blue-400' />
              <span className='leading-relaxed'>{q.question}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Renders getCloudScanEvidence tool invocations.
 */
export function GetCloudScanEvidenceMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result } = getToolState(part)

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='getCloudScanEvidence'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Available checks loaded' : 'Loading available checks...'}
      </span>
    </div>
  )
}

/**
 * Renders getPopulationLists tool invocations.
 */
export function GetPopulationListsMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result } = getToolState(part)

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='getPopulationLists'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Population lists loaded' : 'Loading population lists...'}
      </span>
    </div>
  )
}

/**
 * Renders delegateToScannerAgent tool invocations.
 */
export function DelegateToScannerAgentMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result } = getToolState(part)

  if (isComplete && isToolError(isComplete, result)) {
    return (
      <ToolErrorGuidance
        toolName='delegateToScannerAgent'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Scanner agent completed' : 'Running scanner agent...'}
      </span>
    </div>
  )
}

/**
 * Renders checkPolicyConsistency tool invocations.
 */
export function CheckPolicyConsistencyMessage({ part }: PolicyToolPartProps) {
  const { isComplete } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? 'Consistency check done'
          : 'Checking policy consistency...'}
      </span>
    </div>
  )
}

/**
 * Renders runGuidedTemplate tool invocations with async cloud scan progress.
 */
export function RunGuidedTemplateMessage({ part }: PolicyToolPartProps) {
  const { isComplete, result, args } = getToolState(part)
  const providerRaw = String(
    result?.provider ?? args?.provider ?? ''
  ).toLowerCase()
  const provider =
    providerRaw === 'aws' || providerRaw === 'gcp' || providerRaw === 'azure'
      ? providerRaw
      : null
  const providerLabel = provider ? provider.toUpperCase() : 'Cloud'
  const programId =
    typeof result?.programId === 'string' ? result.programId : null
  const expectedRunId = typeof result?.runId === 'string' ? result.runId : null

  const findingsQuery = useQuery({
    ...trpc.checker.getScanFindings.queryOptions({
      programId: programId ?? '',
      provider: provider ?? 'aws',
      runId: expectedRunId ?? undefined,
    }),
    enabled: isComplete && !!programId && !!provider,
    refetchInterval: (query) => (query.state.data?.isTerminal ? false : 1200),
    refetchIntervalInBackground: true,
  })

  if (!isComplete) {
    return (
      <div className='flex items-center gap-2 py-1 text-xs'>
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        <span className='font-medium text-status-blue-strong'>
          Starting guided scan...
        </span>
      </div>
    )
  }

  if (!result?.success) {
    return (
      <ToolErrorGuidance
        toolName='runGuidedTemplate'
        errorMessage={result?.message ?? 'Unable to start guided scan'}
      />
    )
  }

  const queryData = findingsQuery.data
  const latestRunId = queryData?.runId ?? null
  const isMatchingRun = !expectedRunId || expectedRunId === latestRunId
  const phase = isMatchingRun ? (queryData?.phase ?? 'queued') : 'queued'
  const percent = isMatchingRun ? (queryData?.percent ?? 0) : 0
  const isTerminal = isMatchingRun ? (queryData?.isTerminal ?? false) : false
  const findings = isMatchingRun ? (queryData?.findings ?? []) : []
  const latestMessage =
    findings.length > 0 ? findings[findings.length - 1]?.message : null

  return (
    <div className='py-1 text-xs'>
      <div className='flex items-center gap-2'>
        {isTerminal ? (
          <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-600' />
        ) : (
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin text-status-blue' />
        )}
        <span
          className={
            isTerminal
              ? 'font-medium text-status-green-strong'
              : 'font-medium text-status-blue-strong'
          }
        >
          {isTerminal
            ? `${providerLabel} scan complete`
            : `Running ${providerLabel} scan (${percent}%)`}
        </span>
      </div>
      {!isTerminal && (
        <div className='bg-muted mt-2 h-1.5 w-full overflow-hidden rounded-full'>
          <div
            className='h-1.5 bg-status-blue transition-[width] duration-300 ease-out'
            style={{ width: `${Math.max(3, Math.min(100, percent))}%` }}
          />
        </div>
      )}
      {(latestMessage || phase) && (
        <p className='text-muted-foreground mt-1 text-xs'>
          {latestMessage ?? `Phase: ${phase}`}
        </p>
      )}
      {findings.length > 0 && (
        <div className='mt-1 space-y-0.5'>
          {findings.slice(-3).map((finding: any) => (
            <div key={finding.id} className='text-muted-foreground text-xs'>
              {finding.message}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function GetPageContentMessage({ part }: PolicyToolPartProps) {
  const { isComplete } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Reviewed current page' : 'Reading current page...'}
      </span>
    </div>
  )
}

/**
 * Renders recordClaim tool invocations compactly.
 */
export function RecordClaimMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isError || isToolError(isComplete, result) ? (
        <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
      ) : isComplete ? (
        <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span className='truncate'>
        {isError || isToolError(isComplete, result)
          ? `Claim failed: ${getErrorMessage(result) ?? 'unknown error'}`
          : isComplete
            ? `Claim recorded: ${args.claimText ?? args.claimType ?? 'policy claim'}`
            : `Recording claim: ${args.claimText ?? args.claimType ?? 'policy claim'}`}
      </span>
    </div>
  )
}

/**
 * Renders analyzeGap tool invocations.
 */
export function AnalyzeGapMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='analyzeGap'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const uncovered = result?.uncoveredBlocks?.length ?? 0
  const systems = result?.systemsMentionedButUncovered?.length ?? 0

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <ScanSearch className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? uncovered === 0
            ? 'Gap analysis complete: full coverage'
            : `Gap analysis: ${uncovered} uncovered block${uncovered !== 1 ? 's' : ''}${systems > 0 ? `, ${systems} system${systems !== 1 ? 's' : ''} need claims` : ''}`
          : `Analyzing coverage gaps${args.policySlug ? ` for ${args.policySlug}` : ''}...`}
      </span>
    </div>
  )
}

/**
 * Renders lookupCisBenchmark tool invocations.
 */
export function LookupCisBenchmarkMessage({ part }: PolicyToolPartProps) {
  const { isComplete, args } = getToolState(part)
  const provider = args.provider?.toUpperCase?.() ?? 'Cloud'
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <BookOpen className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? `CIS benchmarks loaded (${provider})`
          : `Looking up CIS benchmarks (${provider})...`}
      </span>
    </div>
  )
}

/**
 * Renders checkPolicyQuality tool invocations.
 */
export function CheckPolicyQualityMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='checkPolicyQuality'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  const passed = result?.passed ?? result?.status === 'PASS'

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {!isComplete ? (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      ) : passed ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
      )}
      <span>
        {!isComplete
          ? `Checking policy quality${args.policySlug ? ` for ${args.policySlug}` : ''}...`
          : passed
            ? 'Policy quality check passed'
            : 'Policy quality issues found'}
      </span>
    </div>
  )
}

/**
 * Renders checkPolicy tool invocations.
 */
export function CheckPolicyMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='checkPolicy'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <ClipboardCheck className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? `Policy check complete${args.policySlug ? ` for ${args.policySlug}` : ''}`
          : `Checking policy${args.policySlug ? ` for ${args.policySlug}` : ''}...`}
      </span>
    </div>
  )
}

/**
 * Renders recordClaims tool invocations.
 */
export function RecordClaimsMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const [expanded, setExpanded] = useState(false)
  type RecordClaimPreview = {
    claimText?: string
    quoteText?: string
    blockId?: string
    claimType?: string
    controlRef?: string
    systemOfRecord?: string
    verification?: string
    confidence?: string
  }
  const claims: RecordClaimPreview[] = Array.isArray(args.claims)
    ? args.claims
    : Array.isArray(args.batch)
      ? args.batch
      : []
  const inputCount = Array.isArray(args.claims)
    ? args.claims.length
    : Array.isArray(args.batch)
      ? args.batch.length
      : null
  // Use the recorded count from the result when available (reflects actual batch state)
  const count =
    typeof result?.recorded === 'number' ? result.recorded : inputCount
  const canExpand = claims.length > 0

  return (
    <div className='py-1 text-xs'>
      <button
        type='button'
        onClick={() => canExpand && setExpanded((v) => !v)}
        className={`text-muted-foreground flex w-full items-center gap-2 ${canExpand ? 'cursor-pointer' : ''}`}
        disabled={!canExpand}
      >
        {isError || isToolError(isComplete, result) ? (
          <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
        ) : isComplete ? (
          <Shield className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        ) : (
          <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
        )}
        <span className='flex-1 text-left'>
          {isError || isToolError(isComplete, result)
            ? 'Claim recording failed'
            : isComplete
              ? `Recorded ${count ?? ''} claims`
              : `Recording ${inputCount ?? ''} claims...`}
        </span>
        {canExpand &&
          (expanded ? (
            <ChevronDown className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ) : (
            <ChevronRight className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
          ))}
      </button>

      {expanded && canExpand && (
        <div className='mt-2 ml-5 space-y-1.5'>
          {claims.map((claim, index) => (
            <div
              key={`${claim.blockId ?? 'claim'}-${claim.claimType ?? index}-${index}`}
              className='flex items-start gap-2'
            >
              <Shield className='mt-0.5 h-3.5 w-3.5 shrink-0 text-cobalt-strong' />
              <div className='text-text-secondary min-w-0 flex-1 text-[12px] leading-relaxed'>
                {claim.claimText ?? claim.quoteText ?? `Claim ${index + 1}`}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Renders delegateToMatrixAgent tool invocations.
 */
export function DelegateToMatrixAgentMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='delegateToMatrixAgent'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? 'Control matrix agent completed'
          : 'Running control matrix agent...'}
      </span>
    </div>
  )
}

/**
 * Renders delegateToSchedulerAgent tool invocations.
 */
export function DelegateToSchedulerAgentMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result } = getToolState(part)

  if (isComplete && (isError || isToolError(isComplete, result))) {
    return (
      <ToolErrorGuidance
        toolName='delegateToSchedulerAgent'
        errorMessage={getErrorMessage(result)}
      />
    )
  }

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? 'Scheduler agent completed'
          : 'Running scheduler agent...'}
      </span>
    </div>
  )
}

/**
 * Renders getProgramState tool invocations.
 */
export function GetProgramStateMessage({ part }: PolicyToolPartProps) {
  const { isComplete } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Program state loaded' : 'Loading program state...'}
      </span>
    </div>
  )
}

/**
 * Renders getCompanyReadiness tool invocations.
 */
export function GetCompanyReadinessMessage({ part }: PolicyToolPartProps) {
  const { isComplete } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete
          ? 'Readiness assessment loaded'
          : 'Assessing company readiness...'}
      </span>
    </div>
  )
}

/**
 * Renders askPolicyQuestion tool invocations.
 */
export function AskPolicyQuestionMessage({ part }: PolicyToolPartProps) {
  const { isComplete, args } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <HelpCircle className='h-3.5 w-3.5 shrink-0 text-blue-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span className='truncate'>
        {isComplete ? 'Question asked' : 'Asking question...'}
        {args.question ? `: ${args.question}` : ''}
      </span>
    </div>
  )
}

/**
 * Renders updateProgramState tool invocations.
 */
export function UpdateProgramStateMessage({ part }: PolicyToolPartProps) {
  const { isComplete } = getToolState(part)
  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span>
        {isComplete ? 'Program state updated' : 'Updating program state...'}
      </span>
    </div>
  )
}
export function TriageDraftClaimMessage({ part }: PolicyToolPartProps) {
  const { isComplete, isError, result, args } = getToolState(part)
  const triageStatus = result?.triageStatus ?? args?.triageStatus ?? null
  const triageNote = result?.triageNote ?? args?.triageNote ?? null
  const draftClaimId = result?.draftClaimId ?? args?.draftClaimId ?? null
  const statusLabel =
    triageStatus === 'accepted'
      ? 'accepted'
      : triageStatus === 'fixed'
        ? 'fixed'
        : triageStatus === 'dismissed'
          ? 'dismissed'
          : triageStatus === 'flagged'
            ? 'flagged'
            : null
  const completeMessage = (() => {
    if (statusLabel && triageNote) return `Claim ${statusLabel}: ${triageNote}`
    if (statusLabel && draftClaimId)
      return `Claim ${statusLabel} (${draftClaimId})`
    if (statusLabel) return `Claim ${statusLabel}`
    return 'Claim triaged'
  })()

  return (
    <div className='text-muted-foreground flex items-center gap-2 py-1 text-xs'>
      {isError ? (
        <CircleAlert className='h-3.5 w-3.5 shrink-0 text-status-orange' />
      ) : isComplete ? (
        <CheckCircle2 className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
      ) : (
        <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
      )}
      <span className='truncate'>
        {isError
          ? 'Triage failed'
          : isComplete
            ? completeMessage
            : 'Triaging claim...'}
      </span>
    </div>
  )
}
